/*
 * Optimizely Feature Experimentation — stable visitor id.
 *
 * One UUID per browser, persisted in localStorage, used as the Optimizely
 * userId for BOTH server-side decisions (forwarded as x-visitor-id) and
 * client-side conversion events. It must never change on login/logout:
 * impressions and conversions attribute to the same visitor or Optimizely
 * reports exposure with zero conversions.
 */

const STORAGE_KEY = 'optimizely_visitor_id'

let inMemoryId = null
let createdThisLoad = false

function generateUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    // RFC 4122 v4 via getRandomValues (older Safari/iOS)
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20
    )}-${hex.slice(20)}`
}

/**
 * Return this browser's stable visitor UUID, creating it on first use.
 * Falls back to a per-pageload in-memory id when storage is unavailable
 * (private mode, blocked site data). Returns null during SSR.
 * @returns {string|null}
 */
export function getVisitorId() {
    if (typeof window === 'undefined') return null
    if (inMemoryId) return inMemoryId

    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored) {
            inMemoryId = stored
            return inMemoryId
        }
        inMemoryId = generateUuid()
        createdThisLoad = true
        window.localStorage.setItem(STORAGE_KEY, inMemoryId)
        return inMemoryId
    } catch (_) {
        // storage blocked (private mode) — keep a per-pageload id
        inMemoryId = inMemoryId || generateUuid()
        createdThisLoad = true
        return inMemoryId
    }
}

/** True when this page load had to create the id — i.e. a first-ever visit. */
export function isFirstVisit() {
    if (typeof window === 'undefined') return false
    getVisitorId()
    return createdThisLoad
}
